import React from "react"
import Header from "../Header"
import Footer from "../Footer"
import WhatsAppFloat from "../WhatsAppFloat"
import { Shield, Eye, Lock, Users, Settings, FileText, Mail, Globe, Building } from "lucide-react"
import { useTranslation } from "react-i18next"

export default function Privacy() {
  const { t } = useTranslation('privacy');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-black">
      <Header />

      <main className="pt-20 md:pt-32 pb-8 md:pb-16">
        <div className="w-full px-3 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Header Section */}
            <div className="text-center mb-8 md:mb-12">
              <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-green-100 rounded-full mb-4 md:mb-6">
                <Shield className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                {t('title')}
              </h1>
              <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-2">
                {t('subtitle')}
              </p>
              <div className="mt-4 md:mt-6 inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">
                <FileText className="w-4 h-4" />
                <span>{t('effectiveDate')}</span>
              </div>
            </div>

            {/* Introduction */}
            <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-8 mb-6 md:mb-8 shadow-sm">
              <p className="text-base md:text-lg text-gray-800 leading-relaxed">
                {t('intro')}
              </p>
            </div>

            {/* Section 1 - Information Collection */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('section1.title')}</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4">{t('section1.intro')}</p>
                <ul className="space-y-2 md:space-y-3">
                  {t('section1.items', { returnObjects: true }).map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-800 text-sm md:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 md:mt-6 p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-blue-800 text-xs md:text-sm">
                    <strong>Important:</strong> {t('section1.note')}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 2 - How We Use Information */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('section2.title')}</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4">{t('section2.intro')}</p>
                <ul className="space-y-2 md:space-y-3">
                  {t('section2.items', { returnObjects: true }).map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-800 text-sm md:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 md:mt-6 p-3 md:p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-purple-800 text-xs md:text-sm">
                    <strong>Location Data:</strong> {t('section2.note')}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 3 - Sharing and Disclosure */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('section3.title')}</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4">{t('section3.intro')}</p>
                <ul className="space-y-2 md:space-y-3">
                  {t('section3.items', { returnObjects: true }).map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-800 text-sm md:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Section 4 - Cookies */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('section4.title')}</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4 text-sm md:text-base">{t('section4.p1')}</p>
                <p className="text-gray-800 text-sm md:text-base">{t('section4.p2')}</p>
              </div>
            </section>

            {/* Section 5 - Data Retention */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 md:w-5 md:h-5 text-teal-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('section5.title')}</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 text-sm md:text-base">{t('section5.text')}</p>
              </div>
            </section>

            {/* Section 6 - Security */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Lock className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('section6.title')}</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 text-sm md:text-base">{t('section6.text')}</p>
              </div>
            </section>

            {/* Section 7 - Your Rights */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('section7.title')}</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4 text-sm md:text-base">{t('section7.intro')}</p>
                <ul className="space-y-2 md:space-y-3">
                  {t('section7.items', { returnObjects: true }).map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-800 text-sm md:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 md:mt-6 p-3 md:p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-green-800 text-xs md:text-sm">{t('section7.note')}</p>
                </div>
              </div>
            </section>

            {/* Section 8 - Changes */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 md:w-5 md:h-5 text-yellow-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('section8.title')}</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 text-sm md:text-base">{t('section8.text')}</p>
              </div>
            </section>

            {/* Section 9 - Contact */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('section9.title')}</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4 text-sm md:text-base">{t('section9.intro')}</p>
                <div className="space-y-2 md:space-y-3">
                  <div className="flex items-center gap-3 p-2 md:p-3 bg-blue-50 rounded-lg">
                    <Mail className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                    <span className="text-blue-800 font-medium text-sm md:text-base">support@trackteam.app</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 md:p-3 bg-green-50 rounded-lg">
                    <Globe className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                    <span className="text-green-800 font-medium text-sm md:text-base">https://www.trackteam.app</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 md:p-3 bg-purple-50 rounded-lg">
                    <Building className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                    <span className="text-purple-800 font-medium text-sm md:text-base">{t('section9.company')}</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer Note */}
            <div className="text-center mt-8 md:mt-12 p-4 md:p-6 bg-gray-50 rounded-xl md:rounded-2xl border border-gray-200">
              <p className="text-gray-600 text-xs md:text-sm">{t('footerNote')}</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  )
}
